import React, { useState, useEffect } from 'react';
import { useFetchData } from 'services/queries';
import NcmModal from './NcmModal';
import { formatNcmCode } from 'utils/formatters/ncmFormatter';

// This component handles the state and logic for the NCM modal
const NcmModalController = ({
  isOpen,
  onClose,
  onSave,
  initialProductId = null,
  initialNcmValues = null // New prop to receive current values
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({
    ncm_code: '',
    ncm_external_code: '',
    reducao_base_pis_cofins: 0,
    majoracao_cofins: 0
  });
  const [isSaving, setIsSaving] = useState(false);
  const [ncmDescription, setNcmDescription] = useState(''); // Store description for edit mode

  // Reset/Initialize state when modal is opened or initial values change
  useEffect(() => {
    if (isOpen) {
      if (initialNcmValues) {
        // Editing existing: Pre-populate form data and start at customize step
        // Keep data as decimal - conversion happens only at UI layer
        setFormData({
          ncm_code: initialNcmValues.ncm_code || '',
          ncm_external_code: initialNcmValues.ncm_external_code || '',
          reducao_base_pis_cofins: initialNcmValues.reducao_base_pis_cofins || 0,
          majoracao_cofins: initialNcmValues.majoracao_cofins || 0
        });
        setSearchTerm(initialNcmValues.ncm_code || ''); // Pre-fill search term
        setDisplaySearchTerm(formatNcmCode(initialNcmValues.ncm_code || ''));
        setNcmDescription(initialNcmValues.ncm_description || 'NCM details not available'); // Set description
        setSelectedRow(null); // Will be set if search results load
        setActiveStep(1); // Start at customize step when editing existing NCM
        setShouldFetch(false); // Don't auto-fetch on open when editing
      } else {
      // Assigning new: Reset everything and start at search step
        setActiveStep(0);
        setSearchTerm("");
        setDisplaySearchTerm("");
        setSelectedRow(null);
        setShouldFetch(false);
        setNcmDescription('');
        setFormData({
          ncm_code: '',
          ncm_external_code: '',
          reducao_base_pis_cofins: 0,
          majoracao_cofins: 0
        });
      }
    }
  }, [isOpen, initialNcmValues]); // Rerun if initialNcmValues changes while open

  // Update display value when searchTerm changes
  useEffect(() => {
    if (searchTerm) {
      try {
        setDisplaySearchTerm(formatNcmCode(searchTerm));
      } catch (error) {
        setDisplaySearchTerm(searchTerm);
      }
    }
  }, [searchTerm]);

  // Search results fetching
  const {
    data: searchResults,
    isLoading: isSearching,
    error: searchError,
    refetch
  } = useFetchData(
    shouldFetch ? `tax/aduaneiras-proxy/${searchTerm.replace(/\D/g, '')}` : null,
    false,
    shouldFetch && searchTerm.replace(/\D/g, '').length >= 8 // Pass a boolean directly instead of an object
  );

  // Handle changes in the NCM search input
  const handleSearchInputChange = (e) => {
    const cleanValue = e.target.value.replace(/\D/g, '').substring(0, 8);
    setSearchTerm(cleanValue);
    setSelectedRow(null); // Reset selection when search term changes
    setNcmDescription(''); // Clear description
    setFormData(prev => ({ // Clear NCM details but keep custom fields
      ...prev,
      ncm_code: '',
      ncm_external_code: ''
    }));
  };

  // Handle changes in the custom form fields (Redução)
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // Value comes already processed from NcmCustomize (as decimal)
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = () => {
    const cleanSearchTerm = searchTerm.replace(/\D/g, '');
    if (!cleanSearchTerm || cleanSearchTerm.length < 8) return;
    setShouldFetch(true);
    setSelectedRow(null);
    setNcmDescription('');
    // Use a timeout to ensure state update happens before refetch
    setTimeout(() => {
      refetch();
    }, 0);
  };

  const handleSelectNcm = (result, index) => {
    setSelectedRow(index);
    const code = result?.ncm?.codigo || '';
    const externalId = result?.id || result?.['@id'] || '';
    const description = result?.ncm?.descricao || 'Description not found';

    setFormData(prev => ({
      ...prev, // Keep existing reducao values
      ncm_code: code,
      ncm_external_code: externalId
    }));
    setNcmDescription(description); // Store description for review step
  };

  const handleContinue = () => {
    // Move to the next step based on current step
    if (activeStep === 0) {
      // From Search to Customize: Only continue if an NCM is selected
      if (selectedRow !== null || formData.ncm_code) {
        setActiveStep(1);
      } else {
        console.warn("Please select an NCM code first.");
      }
    } else if (activeStep === 1) {
      // From Customize to Review
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    // Move back to the previous step based on current step
    if (activeStep === 1) {
      setActiveStep(0); // From Customize to Search
    } else if (activeStep === 2) {
      setActiveStep(1); // From Review to Customize
    }
  };

  const handleSave = async () => {
    // Add validation if needed for mva/reducao fields before saving
    if (!formData.ncm_code) {
      console.error("NCM code is required.");
      return;
    }

    setIsSaving(true);
    try {
      await onSave(initialProductId, formData);
      handleClose(); // Close modal on success
    } catch (error) {
      console.error("Error saving NCM data:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state fully on close, regardless of initial values
    setActiveStep(0);
    setShouldFetch(false);
    setSearchTerm("");
    setDisplaySearchTerm("");
    setSelectedRow(null);
    setNcmDescription('');
    setFormData({
      ncm_code: '',
      ncm_external_code: '',
      reducao_base_pis_cofins: 0,
      majoracao_cofins: 0
    });
  };

  // Updated stepper steps for the 3-step flow
  const steps = [
    {
      label: 'Search',
      description: 'Find the appropriate NCM code'
    },
    {
      label: 'Customize',
      description: 'Set custom tax values'
    },
    {
      label: 'Review',
      description: 'Review and confirm'
    }
  ];

  return (
    <NcmModal
      isOpen={isOpen}
      onClose={handleClose}
      activeStep={activeStep}
      steps={steps}
      searchTerm={searchTerm}
      displaySearchTerm={displaySearchTerm}
      handleSearchInputChange={handleSearchInputChange}
      handleFormChange={handleFormChange}
      handleSearch={handleSearch}
      isSearching={isSearching}
      searchError={searchError}
      searchResults={searchResults}
      selectedRow={selectedRow}
      handleSelectNcm={handleSelectNcm}
      handleContinue={handleContinue}
      handleBack={handleBack}
      handleSave={handleSave}
      isSaving={isSaving}
      formData={formData}
      ncmDescription={ncmDescription}
    />
  );
};

export default NcmModalController;
